/**
 * ChatGPT Ads measurement pixel (`oaiq`).
 *
 * The SDK is injected by `gatsby-ssr.jsx` and only after Osano reports
 * MARKETING consent; the queue installed alongside it buffers calls made
 * before the script lands. Everything here no-ops when the pixel is disabled
 * (no `OPENAI_PIXEL_ID`) or the visitor blocks the script, so callers never
 * need to guard.
 */

declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

export type OpenAiAdsContent = {
  id?: string;
  name?: string;
  /** Non-empty category such as `product`, `plan`, or `page`. */
  content_type?: string;
  quantity?: number;
  amount?: number;
  currency?: string;
};

/** Commerce payload sent alongside a custom conversion. */
export type OpenAiAdsCustomData = {
  /** Minor units - see `toMinorUnits`. */
  amount?: number;
  currency?: string;
  contents?: OpenAiAdsContent[];
};

export type OpenAiAdsOptions = {
  /** Shared id that lets a later Conversions API leg dedupe against the pixel. */
  event_id?: string;
  opt_out?: boolean;
};

/**
 * Monetary values must be integers in the currency's minor unit: $25.99 is
 * sent as 2599.
 */
export const toMinorUnits = (amount: number): number =>
  Math.round(Number(amount) * 100);

export namespace openAiAds {
  /**
   * Fires a custom conversion. Ads Manager keys the conversion off
   * `custom_event_name`, while the commerce fields (`amount`, `currency`,
   * `contents`) carry the product names and revenue the conversion reports on.
   */
  export const measureCustom = (
    customEventName: string,
    data: OpenAiAdsCustomData = {},
    options?: OpenAiAdsOptions,
  ): void => {
    try {
      if (typeof window === 'undefined' || !window.oaiq) {
        return;
      }

      window.oaiq(
        'measure',
        'custom',
        { type: 'custom', ...data },
        { custom_event_name: customEventName, ...options },
      );
    } catch {
      // never let tracking break the page
    }
  };
}
